import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { useRemoveClientDashboard } from '@/queries/clientDashboards'
import { extractErrorMessage } from '@/queries/ticketMutations'
import type { ClientDashboard } from '@/types/clientDashboard'

export function RemoveClientDialog({ dashboard }: { dashboard: ClientDashboard }) {
  const [open, setOpen] = useState(false)
  const remove = useRemoveClientDashboard(dashboard.companyId)
  const navigate = useNavigate()

  async function confirm() {
    try {
      await remove.mutateAsync()
      toast.success(`${dashboard.companyName}'s dashboard removed`)
      setOpen(false)
      navigate({ to: '/clients' as never })
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not remove this client'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="destructive">Remove dashboard</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Remove dashboard</DialogTitle>
          <DialogDescription>
            Remove {dashboard.companyName}'s dashboard? Its account team and cadence are kept and come back if you add it again.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={confirm} disabled={remove.isPending}>
            Remove
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
